import { DatePicker, Select, Space, Typography } from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useGetMarketplaces } from '../../../../utils/api/marketplaces/useGetMarketplaces';
import { useGetStores } from '../../../../utils/api/stores/useGetStores';
import {
  OFFER_CHANNEL_COUNTRY_LABELS,
  OFFER_CHANNEL_ONLY_COUNTRIES,
  OFFER_MP_STATUS_OPTIONS,
  OFFER_PUBLICATION_STATE_OPTIONS,
  OFFER_PUBLISH_STATUS_OPTIONS,
} from '../../../../utils/const/mpOffers';
import { useOffersFilters } from '../../../../utils/zustand';

const { Text } = Typography;

// Pays des stores et des channels (ex. Allegro CZ), plus les enseignes vendues
// sur un channel (ex. Bricoman sur LM FR).
const buildCountryOptions = (stores) => {
  const storeCountries = (stores || [])
    .map((store) => store.country)
    .filter(Boolean);
  const countries = [
    ...new Set([...storeCountries, ...OFFER_CHANNEL_ONLY_COUNTRIES]),
  ].sort();
  const brandOptions = Object.entries(OFFER_CHANNEL_COUNTRY_LABELS).map(
    ([value, label]) => ({ value, label })
  );
  return [
    ...countries.map((country) => ({ value: country, label: country })),
    ...brandOptions,
  ];
};

const MpOffersFilters = () => {
  const { filters, setFilter, removeFilter, setFilters } = useOffersFilters();
  const { marketplaces } = useGetMarketplaces({ store__id: filters.store });
  const { stores, isLoading: isStoresLoading } = useGetStores({
    marketplace__id: filters.marketplace,
  });
  const { stores: allStores } = useGetStores();

  const handleMarketplaceChange = (value) => {
    const { marketplace: _, store: __, ...rest } = filters;
    setFilters(value == null ? rest : { ...rest, marketplace: value });
  };

  const handleDiscountRangeChange = (dates) => {
    const [from, to] = dates || [];
    const {
      discount_start_at_after: _,
      discount_end_at_before: __,
      ...rest
    } = filters;
    setFilters({
      ...rest,
      ...(from && {
        discount_start_at_after: from.startOf('day').toISOString(),
      }),
      ...(to && { discount_end_at_before: to.endOf('day').toISOString() }),
    });
  };

  const discountRange = [
    filters.discount_start_at_after
      ? dayjs(filters.discount_start_at_after)
      : null,
    filters.discount_end_at_before
      ? dayjs(filters.discount_end_at_before)
      : null,
  ];

  const marketplaceOptions = (marketplaces || []).map((mp) => ({
    label: mp.name,
    value: mp.id,
  }));

  const storeOptions = (stores || []).map((store) => ({
    label: store.name,
    value: store.id,
  }));

  const countryOptions = buildCountryOptions(allStores);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Text strong>Marketplace</Text>
        <Select
          size="small"
          placeholder="Toutes"
          style={{ width: '100%', marginTop: 4 }}
          allowClear
          showSearch
          optionFilterProp="label"
          value={filters.marketplace ?? null}
          options={marketplaceOptions}
          onChange={handleMarketplaceChange}
        />
      </div>
      <div>
        <Text strong>Store</Text>
        <Select
          size="small"
          placeholder="Tous"
          style={{ width: '100%', marginTop: 4 }}
          allowClear
          showSearch
          optionFilterProp="label"
          loading={isStoresLoading}
          value={filters.store ?? null}
          options={storeOptions}
          onChange={(value) => {
            if (value == null) removeFilter('store');
            else setFilter('store', value);
          }}
        />
      </div>
      <div>
        <Text strong>Pays</Text>
        <Select
          size="small"
          placeholder="Tous"
          style={{ width: '100%', marginTop: 4 }}
          allowClear
          showSearch
          optionFilterProp="label"
          value={filters.country ?? null}
          options={countryOptions}
          onChange={(value) => {
            if (value == null) removeFilter('country');
            else setFilter('country', value);
          }}
        />
      </div>
      <div>
        <Text strong>Statut offre</Text>
        <Select
          size="small"
          placeholder="Tous"
          style={{ width: '100%', marginTop: 4 }}
          allowClear
          value={filters.mp_status ?? null}
          options={OFFER_MP_STATUS_OPTIONS}
          onChange={(value) => {
            if (value == null) removeFilter('mp_status');
            else setFilter('mp_status', value);
          }}
        />
      </div>
      <div>
        <Text strong>Statut publication</Text>
        <Select
          size="small"
          placeholder="Tous"
          style={{ width: '100%', marginTop: 4 }}
          allowClear
          value={filters.publish_status ?? null}
          options={OFFER_PUBLISH_STATUS_OPTIONS}
          onChange={(value) => {
            if (value == null) removeFilter('publish_status');
            else setFilter('publish_status', value);
          }}
        />
      </div>
      <div>
        <Text strong>État publication</Text>
        <Select
          size="small"
          placeholder="Tous"
          style={{ width: '100%', marginTop: 4 }}
          allowClear
          value={filters.publication_state ?? null}
          options={OFFER_PUBLICATION_STATE_OPTIONS}
          onChange={(value) => {
            if (value == null) removeFilter('publication_state');
            else setFilter('publication_state', value);
          }}
        />
      </div>
      <div>
        <Text strong>Dates de promo</Text>
        <DatePicker.RangePicker
          size="small"
          style={{ width: '100%', marginTop: 4 }}
          format="DD/MM/YYYY"
          placeholder={['Du', 'Au']}
          allowEmpty={[true, true]}
          value={discountRange}
          onChange={handleDiscountRangeChange}
        />
      </div>
    </Space>
  );
};

export default MpOffersFilters;
